import React, { useEffect, useMemo, useState } from "react";
import { usePermissions } from "react-admin";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";

import {
  Paper,
  Typography,
  Stack,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import { db, auth } from "../../../firebase/firebase";
import { loadAssignmentsMap } from "../../earningsImport/assignmentUtils";

import {
  buildCarIntervals,
  computeIdleFromIntervals,
  formatSeg,
  monthToRange,
  normalizeDay,
  toKey,
} from "./idleReportUtils";
import { exportCarIdleToExcel } from "./carIdleExportExcel";
import { exportCarIdleToPDF } from "./carIdleExportPDF";

/** ---------- helpers ---------- */
function toDateMaybe(ts) {
  if (!ts) return null;
  if (typeof ts?.toDate === "function") return ts.toDate();
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

function dayBefore(d) {
  const x = new Date(d);
  x.setDate(x.getDate() - 1);
  return x;
}

function iterDaysInclusive(start, end, cb) {
  const dayMs = 24 * 60 * 60 * 1000;
  const s = normalizeDay(start).getTime();
  const e = normalizeDay(end).getTime();
  for (let t = s; t <= e; t += dayMs) cb(new Date(t));
}

function overlapsMonth(markFrom, markTo, monthStartKey, monthEndKey) {
  return markFrom <= monthEndKey && markTo >= monthStartKey;
}

function clampKey(k, minK, maxK) {
  if (k < minK) return minK;
  if (k > maxK) return maxK;
  return k;
}

/** ---------- component ---------- */
export default function CarIdleReport() {
  const { permissions } = usePermissions();
  const role = String(permissions || "").toLowerCase();
  const isAdmin = role === "admin";

  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  const [month, setMonth] = useState(defaultMonth);
  const [companyFilter, setCompanyFilter] = useState("ALL");
  const [companyOptions, setCompanyOptions] = useState([]);

  // ✅ default: show idle cars only
  const [showAllCars, setShowAllCars] = useState(false);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  // manual marks cache (month-scoped)
  const [marksByCar, setMarksByCar] = useState(new Map());
  const [marksLoading, setMarksLoading] = useState(false);

  // dialog state
  const [open, setOpen] = useState(false);
  const [activeCar, setActiveCar] = useState(null);
  const [fromDay, setFromDay] = useState("");
  const [toDay, setToDay] = useState("");
  const [reason, setReason] = useState("CAR_PARKED");
  const [notes, setNotes] = useState("");

  const monthStartKey = useMemo(() => `${month}-01`, [month]);
  const monthEndKey = useMemo(() => toKey(monthToRange(month).end), [month]);

  async function loadMarksForMonth() {
    setMarksLoading(true);
    try {
      // ✅ only one where filter (no composite index)
      const q1 = query(collection(db, "carIdleManual"), where("fromDay", "<=", monthEndKey));
      const snap = await getDocs(q1);

      const map = new Map();
      snap.forEach((d) => {
        const m = { id: d.id, ...(d.data() || {}) };
        const car = String(m.carNumber || "").trim();
        if (!car) return;

        const fd = String(m.fromDay || "").trim();
        const td = String(m.toDay || "").trim();
        if (!fd || !td) return;

        if (!overlapsMonth(fd, td, monthStartKey, monthEndKey)) return;

        if (!map.has(car)) map.set(car, []);
        map.get(car).push(m);
      });

      map.forEach((arr) => arr.sort((a, b) => String(a.fromDay || "").localeCompare(String(b.fromDay || ""))));
      setMarksByCar(map);
    } finally {
      setMarksLoading(false);
    }
  }

  useEffect(() => {
    loadMarksForMonth().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, monthEndKey]);

  // Build the unified report rows
  useEffect(() => {
    let alive = true;

    async function run() {
      setLoading(true);
      setError("");

      try {
        const { start, end } = monthToRange(month);

        // vehicles master list
        const vehiclesSnap = await getDocs(collection(db, "vehicles"));
        const vehicles = [];
        const companiesSet = new Set();

        vehiclesSnap.forEach((docSnap) => {
          const v = docSnap.data() || {};
          const vehicleNumber = String(v.vehicleNumber || "").trim();
          if (!vehicleNumber) return;

          const company = String(v.company || "").trim();
          if (company) companiesSet.add(company);

          vehicles.push({
            vehicleNumber,
            company,
            vehicleType: String(v.vehicleType || "").trim(),
            createdAt: toDateMaybe(v.createdAt),
            soldAt: toDateMaybe(v.soldAt),
          });
        });

        if (alive) setCompanyOptions(Array.from(companiesSet).sort());

        // assignments history
        const assignmentsMap = await loadAssignmentsMap();
        const byCar = buildCarIntervals(assignmentsMap);

        const out = [];

        for (const v of vehicles) {
          const carNumber = v.vehicleNumber;
          const company = v.company || "";
          if (companyFilter !== "ALL" && company !== companyFilter) continue;

          // sold filter
          if (v.soldAt) {
            const soldDay = normalizeDay(v.soldAt);
            const monthStart = normalizeDay(start);
            if (soldDay <= monthStart) continue;
          }

          // effective range for this car
          let effectiveStart = start;
          let effectiveEnd = end;

          if (v.createdAt) {
            const createdDay = normalizeDay(v.createdAt);
            if (createdDay > normalizeDay(effectiveEnd)) continue;
            if (createdDay > normalizeDay(effectiveStart)) effectiveStart = createdDay;
          }

          if (v.soldAt) {
            const soldDay = normalizeDay(v.soldAt);
            if (soldDay <= normalizeDay(effectiveEnd)) {
              effectiveEnd = dayBefore(soldDay);
              if (normalizeDay(effectiveEnd) < normalizeDay(effectiveStart)) continue;
            }
          }

          const effStartKey = toKey(effectiveStart);
          const effEndKey = toKey(effectiveEnd);

          const intervals = byCar.get(carNumber) || [];
          const calc = computeIdleFromIntervals({ intervals, rangeStart: effectiveStart, rangeEnd: effectiveEnd });

          // Assigned days (day-level)
          const assignedDaySet = new Set();
          calc.mergedAssignedSegments.forEach((seg) => {
            iterDaysInclusive(seg.start, seg.end, (d) => assignedDaySet.add(toKey(d)));
          });

          // Unassigned idle days (day-level)
          const unassignedIdleDaySet = new Set();
          calc.idleSegments.forEach((seg) => {
            iterDaysInclusive(seg.start, seg.end, (d) => unassignedIdleDaySet.add(toKey(d)));
          });

          // Manual marked idle days (day-level) — clamp to effective range + month
          const manualMarks = marksByCar.get(carNumber) || [];
          const manualMarkedDaySet = new Set();

          if (manualMarks.length) {
            manualMarks.forEach((m) => {
              const fd = String(m.fromDay || "").trim();
              const td = String(m.toDay || "").trim();
              if (!fd || !td) return;

              // clamp to both car effective range and selected month
              const cFromKey = clampKey(fd, effStartKey, monthEndKey);
              const cToKey = clampKey(td, monthStartKey, effEndKey);
              if (cToKey < cFromKey) return;

              const fromD = new Date(cFromKey + "T00:00:00");
              const toD = new Date(cToKey + "T00:00:00");
              iterDaysInclusive(fromD, toD, (d) => manualMarkedDaySet.add(toKey(d)));
            });
          }

          // Total idle = union(unassigned, manual)
          const totalIdleSet = new Set([...unassignedIdleDaySet, ...manualMarkedDaySet]);

          const totalDays = calc.totalDays;
          const assignedDays = assignedDaySet.size;
          const unassignedIdleDays = unassignedIdleDaySet.size;
          const manualIdleDays = manualMarkedDaySet.size;
          const totalIdleDays = totalIdleSet.size;
          const utilization = totalDays ? (totalDays - totalIdleDays) / totalDays : 0;

          // details (short)
          const unassignedText = calc.idleSegments.slice(0, 2).map(formatSeg).join(" | ");
          const manualText = manualMarks
            .slice(0, 2)
            .map((m) => `${m.fromDay}→${m.toDay}${m.reason ? ` (${m.reason})` : ""}`)
            .join(" | ");

          out.push({
            carNumber,
            company,
            vehicleType: v.vehicleType || "",
            totalDays,
            assignedDays,
            unassignedIdleDays,
            manualIdleDays,
            totalIdleDays,
            utilization,
            idleDetailsText: [
              unassignedText ? `U: ${unassignedText}` : "",
              manualText ? `M: ${manualText}` : "",
            ].filter(Boolean).join("   •   ") || "—",
            manualMarksCount: manualMarks.length,
          });
        }

        // sort by most idle first
        out.sort((a, b) => b.totalIdleDays - a.totalIdleDays);

        // ✅ default filter: show only idle cars (utilization < 100%)
        const finalRows = showAllCars ? out : out.filter((r) => r.totalIdleDays > 0);

        if (alive) setRows(finalRows);
      } catch (e) {
        console.error(e);
        if (alive) setError(e?.message || "Failed to build car idle report");
      } finally {
        if (alive) setLoading(false);
      }
    }

    run();
    return () => { alive = false; };
  }, [month, companyFilter, marksByCar, showAllCars, monthEndKey, monthStartKey]);

  // ✅ Correct summary: show BOTH car counts and day totals
  const summary = useMemo(() => {
    const idleCars = rows.length;
    const unassignedIdleCars = rows.filter((r) => r.unassignedIdleDays > 0).length;
    const manualIdleCars = rows.filter((r) => r.manualIdleDays > 0).length;

    const unassignedIdleDaysTotal = rows.reduce((s, r) => s + (r.unassignedIdleDays || 0), 0);
    const manualIdleDaysTotal = rows.reduce((s, r) => s + (r.manualIdleDays || 0), 0);
    const totalIdleDaysTotal = rows.reduce((s, r) => s + (r.totalIdleDays || 0), 0);

    return {
      idleCars,
      unassignedIdleCars,
      manualIdleCars,
      unassignedIdleDaysTotal,
      manualIdleDaysTotal,
      totalIdleDaysTotal,
    };
  }, [rows]);

  function openDialogForCar(row) {
    setActiveCar(row);
    setFromDay(`${month}-01`);
    setToDay(`${month}-01`);
    setReason("CAR_PARKED");
    setNotes("");
    setOpen(true);
  }

  async function saveMark() {
    if (!isAdmin) return;
    if (!activeCar?.carNumber) return;

    const fd = String(fromDay || "").trim();
    const td = String(toDay || "").trim();
    if (!fd || !td) return alert("Please select from/to dates");
    if (td < fd) return alert("To date cannot be before From date");

    const userEmail = auth?.currentUser?.email || "";

    await addDoc(collection(db, "carIdleManual"), {
      carNumber: activeCar.carNumber,
      company: activeCar.company || "",
      vehicleType: activeCar.vehicleType || "",
      fromDay: fd,
      toDay: td,
      reason,
      notes: notes || "",
      createdAt: serverTimestamp(),
      createdBy: userEmail,
      updatedAt: serverTimestamp(),
      updatedBy: userEmail,
    });

    await loadMarksForMonth();
    setOpen(false);
  }

  async function removeMark(markId) {
    if (!isAdmin) return;
    await deleteDoc(doc(db, "carIdleManual", markId));
    await loadMarksForMonth();
  }

  function marksForCarThisMonth(carNumber) {
    const all = marksByCar.get(carNumber) || [];
    return all.filter((m) => overlapsMonth(String(m.fromDay || ""), String(m.toDay || ""), monthStartKey, monthEndKey));
  }

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            🚗 Car Idle Report (Idle Cars Only)
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            Combines: <b>Unassigned idle</b> (no driver assigned) + <b>Manual marked idle</b> (parking/leave/maintenance).
            Manual marks are stored in <code>carIdleManual</code> and do <b>not</b> affect CarStatementUI.
          </Typography>

          <Divider />

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <label>
              Month:&nbsp;
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            </label>

            <label>
              Company:&nbsp;
              <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}>
                <option value="ALL">All Companies</option>
                {companyOptions.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <Button
  variant="contained"
  disabled={!rows.length}
  onClick={() =>
    exportCarIdleToExcel(rows, summary, {
      month,
      companyLabel: companyFilter === "ALL" ? "All Companies" : companyFilter,
      generatedAt: new Date(),
    })
  }
>
  Export Excel
</Button>

<Button
  variant="outlined"
  disabled={!rows.length}
  onClick={() =>
    exportCarIdleToPDF(rows, summary, {
      month,
      companyLabel: companyFilter === "ALL" ? "All Companies" : companyFilter,
      generatedAt: new Date(),
    })
  }
>
  Export PDF
</Button>

            <FormControlLabel
              control={<Checkbox checked={showAllCars} onChange={(e) => setShowAllCars(e.target.checked)} />}
              label="Show fully utilized cars"
            />

            {marksLoading && <Typography variant="body2" sx={{ opacity: 0.7 }}>Loading marks…</Typography>}
            {loading && <Typography variant="body2">Loading…</Typography>}
            {error && <Typography variant="body2" sx={{ color: "crimson" }}>{error}</Typography>}
          </Stack>

          {/* ✅ Correct summary chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            <Chip label={`Idle Cars: ${summary.idleCars}`} />
            <Chip label={`Unassigned Idle Cars: ${summary.unassignedIdleCars}`} />
            <Chip label={`Manual Idle Cars: ${summary.manualIdleCars}`} />
            <Chip label={`Unassigned Idle Days (sum): ${summary.unassignedIdleDaysTotal}`} />
            <Chip label={`Manual Idle Days (sum): ${summary.manualIdleDaysTotal}`} />
            <Chip label={`Total Idle Days (sum): ${summary.totalIdleDaysTotal}`} />
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={1} sx={{ borderRadius: 2 }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 260px)" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {[
                  "Car",
                  "Company",
                  "Model",
                  "Total Days",
                  "Assigned Days",
                  "Unassigned Idle",
                  "Manual Idle",
                  "Total Idle",
                  "Utilization",
                  "Idle Details",
                  "Action",
                ].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.carNumber} hover>
                  <TableCell sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>{r.carNumber}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{r.company}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{r.vehicleType}</TableCell>

                  <TableCell>{r.totalDays}</TableCell>
                  <TableCell>{r.assignedDays}</TableCell>

                  <TableCell sx={{ color: r.unassignedIdleDays >= 10 ? "crimson" : "inherit", fontWeight: r.unassignedIdleDays ? 700 : 400 }}>
                    {r.unassignedIdleDays}
                  </TableCell>

                  <TableCell sx={{ color: r.manualIdleDays >= 10 ? "crimson" : "inherit", fontWeight: r.manualIdleDays ? 700 : 400 }}>
                    {r.manualIdleDays}
                  </TableCell>

                  <TableCell sx={{ color: r.totalIdleDays >= 10 ? "crimson" : "inherit", fontWeight: 800 }}>
                    {r.totalIdleDays}
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>{(r.utilization * 100).toFixed(0)}%</TableCell>

                  <TableCell sx={{ minWidth: 420 }}>{r.idleDetailsText || "—"}</TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {isAdmin ? (
                      <Button variant="outlined" size="small" onClick={() => openDialogForCar(r)}>
                        Mark Idle
                      </Button>
                    ) : (
                      <Typography variant="body2" sx={{ opacity: 0.65 }}>
                        {r.manualMarksCount ? `${r.manualMarksCount} mark(s)` : "—"}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} sx={{ py: 3, opacity: 0.7 }}>
                    No idle cars found for this selection.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* --------- Admin dialog --------- */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Manual Idle Marking</DialogTitle>
        <DialogContent dividers>
          {activeCar && (
            <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
              Car: <b>{activeCar.carNumber}</b> • {activeCar.vehicleType} • {activeCar.company}
            </Typography>
          )}

          <Typography variant="body2" sx={{ fontWeight: 800, mb: 1 }}>
            Existing marks for {month}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {activeCar && marksForCarThisMonth(activeCar.carNumber).length ? (
              marksForCarThisMonth(activeCar.carNumber).map((m) => (
                <Chip
                  key={m.id}
                  label={`${m.fromDay} → ${m.toDay} • ${m.reason || "OTHER"}`}
                  onDelete={isAdmin ? () => removeMark(m.id) : undefined}
                  variant="outlined"
                  size="small"
                />
              ))
            ) : (
              <Typography variant="body2" sx={{ opacity: 0.6 }}>
                No marks for this car in {month}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
            <TextField
              type="date"
              label="From"
              value={fromDay}
              onChange={(e) => setFromDay(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              type="date"
              label="To"
              value={toDay}
              onChange={(e) => setToDay(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              select
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              size="small"
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="CAR_PARKED">CAR_PARKED</MenuItem>
              <MenuItem value="DRIVER_ON_LEAVE">DRIVER_ON_LEAVE</MenuItem>
              <MenuItem value="MAINTENANCE">MAINTENANCE</MenuItem>
              <MenuItem value="NO_DEMAND">NO_DEMAND</MenuItem>
              <MenuItem value="OTHER">OTHER</MenuItem>
            </TextField>
          </Stack>

          <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          <Button variant="contained" onClick={saveMark} disabled={!isAdmin}>
            Save Mark
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}